import { IconDownload, IconEdit, IconQrCode } from '@myplayer/ui';
import { useState, type ReactElement } from 'react';
import {
  ActionOverflowMenu,
  Badge,
  Button,
  CardRow,
  Select,
  ToastNotification,
} from './components/ui/primitives.js';
import {
  QR_EXPORT_FORMATS,
  QR_X_DIMENSION_OPTIONS,
  type GeneratedGs1QrPrintAssets,
  type QrExportFormat,
} from './qr-generator.js';
import { QrRuleViolationAlert, QrStyleEditorModal } from './qr-style-modal.js';
import {
  QR_BACKGROUND_PRESETS,
  QR_DOT_SHAPE_OPTIONS,
  QR_FOREGROUND_PRESETS,
  STYLED_QR_DISCLAIMER_LABEL,
  type QrStyleConfig,
  type QrStyleRuleEvaluation,
} from './qr-style-rules.js';

function QrXDimensionSelect(p: {
  readonly selectId: string;
  readonly xDimensionMm: number;
  readonly onChangeXDimensionMm: (nextMm: number) => void;
}): ReactElement {
  return (
    <Select
      id={p.selectId}
      aria-label="X-dimension (mm)"
      data-testid="qr-x-dimension-select"
      value={p.xDimensionMm.toFixed(3)}
      onChange={(e) => p.onChangeXDimensionMm(Number(e.target.value))}
      className="w-full max-w-full sm:max-w-[260px]"
    >
      {QR_X_DIMENSION_OPTIONS.map((opt) => (
        <option key={opt.value.toFixed(3)} value={opt.value.toFixed(3)}>
          {opt.label}
        </option>
      ))}
    </Select>
  );
}

function QrPrintedDimensionsRow(p: {
  readonly assets: GeneratedGs1QrPrintAssets | null;
  readonly className?: string;
}): ReactElement | null {
  if (p.assets === null) return null;
  return (
    <CardRow
      label="Printed size (mm)"
      {...(p.className !== undefined ? { className: p.className } : {})}
    >
      <span
        data-testid="qr-print-dimensions"
        className="break-words text-right font-semibold tabular-nums"
      >
        {`${p.assets.physicalWidthMm.toFixed(1)} mm × ${p.assets.physicalHeightMm.toFixed(1)} mm (X = ${p.assets.xDimensionMm.toFixed(3)} mm, 4X quiet zone)`}
      </span>
    </CardRow>
  );
}

function QrDownloadButtons(p: {
  readonly disabled?: boolean | undefined;
  readonly onDownload: (format: QrExportFormat) => void;
  readonly onOpenQrCheck?: (() => void) | undefined;
  readonly onOpenEditStyle?: (() => void) | undefined;
}): ReactElement {
  const secondaryFormats = QR_EXPORT_FORMATS.filter((fmt) => fmt !== 'SVG');
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {p.onOpenEditStyle !== undefined ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          data-testid="open-qr-style-modal-button"
          onClick={p.onOpenEditStyle}
        >
          <IconEdit />
          <span>Edit style</span>
        </Button>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={p.disabled}
          data-testid="download-qr-svg-button"
          onClick={() => p.onDownload('SVG')}
        >
          <IconDownload />
          <span>Download SVG</span>
        </Button>
      )}
      <ActionOverflowMenu label="More QR export actions" disabled={p.disabled}>
        {p.onOpenEditStyle !== undefined && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={p.disabled}
            data-testid="download-qr-svg-button"
            onClick={() => p.onDownload('SVG')}
          >
            <IconDownload />
            <span>Download SVG</span>
          </Button>
        )}
        {secondaryFormats.map((fmt) => (
          <Button
            key={fmt}
            type="button"
            size="sm"
            variant="secondary"
            disabled={p.disabled}
            data-testid={`download-qr-${fmt.toLowerCase()}-button`}
            onClick={() => p.onDownload(fmt)}
          >
            <IconDownload />
            <span>Download {fmt}</span>
          </Button>
        ))}
        {p.onOpenQrCheck !== undefined && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            data-testid="open-qr-check-button"
            onClick={p.onOpenQrCheck}
          >
            <IconQrCode />
            <span>Check printed QR</span>
          </Button>
        )}
      </ActionOverflowMenu>
    </div>
  );
}

export function Gs1PrintTabPanel(p: {
  readonly cardTestId: string;
  readonly gs1Assets: GeneratedGs1QrPrintAssets | null;
  readonly resolvedQrUrl: string;
  readonly xDimensionMm: number;
  readonly onChangeXDimensionMm: (nextMm: number) => void;
  readonly onDownload: (format: QrExportFormat) => void;
  readonly onOpenQrCheck?: (() => void) | undefined;
}): ReactElement {
  const { gs1Assets, resolvedQrUrl } = p;
  return (
    <div data-testid="qr-panel-gs1-print" className="space-y-4">
      <div className="flex flex-col items-center gap-5 border-t border-subtle pt-4 sm:flex-row sm:items-start">
        {gs1Assets !== null && (
          <div
            data-testid="qr-svg-preview"
            className="h-32 w-32 shrink-0 rounded-control border border-subtle bg-white p-2 [&>svg]:h-full [&>svg]:w-full"
            dangerouslySetInnerHTML={{ __html: gs1Assets.svg }}
          />
        )}
        <div className="min-w-0 w-full flex-1 self-stretch divide-y divide-subtle">
          <CardRow label="QR code link" className="pt-0">
            {resolvedQrUrl.length > 0 ? (
              <a
                href={resolvedQrUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="qr-canonical-url"
                className="truncate font-mono text-[12px] text-fg underline decoration-subtle underline-offset-2 hover:text-brand"
              >
                {resolvedQrUrl}
              </a>
            ) : (
              <code
                data-testid="qr-canonical-url"
                className="truncate font-mono text-[12px] text-fg"
              >
                Set VITE_RESOLVER_HOST in .env to generate QR URLs
              </code>
            )}
          </CardRow>
          <CardRow label="X-dimension (size)">
            <QrXDimensionSelect
              selectId={`${p.cardTestId}-gs1-x-dim`}
              xDimensionMm={p.xDimensionMm}
              onChangeXDimensionMm={p.onChangeXDimensionMm}
            />
          </CardRow>
          <CardRow label="Print format">
            <span>100% black on white &middot; 4X quiet zone</span>
          </CardRow>
          <QrPrintedDimensionsRow assets={gs1Assets} className="pb-0" />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-subtle pt-3">
        <span className="text-[11px] text-fg-secondary">
          Standard retail packaging (checkout &amp; phone)
        </span>
        <QrDownloadButtons onDownload={p.onDownload} onOpenQrCheck={p.onOpenQrCheck} />
      </div>
    </div>
  );
}

export function StyledQrTabPanel(props: {
  readonly cardTestId: string;
  readonly style: QrStyleConfig;
  readonly styledAssets: GeneratedGs1QrPrintAssets | null;
  readonly ruleEvaluation: QrStyleRuleEvaluation;
  readonly savedNotice: string | null;
  readonly xDimensionMm: number;
  readonly onChangeXDimensionMm: (nextMm: number) => void;
  readonly onUpdateStyle: (patch: Partial<QrStyleConfig>) => void;
  readonly onResetSafeDefaults: () => void;
  readonly onSaveStyled: () => void;
  readonly onDownloadStyled: (format: QrExportFormat) => void;
}): ReactElement {
  const { cardTestId, style, styledAssets, ruleEvaluation, savedNotice } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const findLabel = (
    list: readonly { readonly hex: string; readonly label: string }[],
    hex: string,
  ) => list.find((p) => p.hex.toLowerCase() === hex.toLowerCase())?.label ?? hex;
  const fgLabel = findLabel(QR_FOREGROUND_PRESETS, style.fgHex);
  const bgLabel = findLabel(QR_BACKGROUND_PRESETS, style.bgHex);
  const dotShapeLabel =
    QR_DOT_SHAPE_OPTIONS.find((o) => o.value === style.dotShape)?.label ?? style.dotShape;
  const disableActions = !ruleEvaluation.valid || styledAssets === null;

  return (
    <div data-testid="qr-panel-styled" className="space-y-4">
      <div className="flex flex-col items-center gap-5 border-t border-subtle pt-4 sm:flex-row sm:items-start">
        {styledAssets !== null && (
          <div
            data-testid="qr-svg-preview"
            className="h-32 w-32 shrink-0 rounded-control border border-subtle bg-white p-2 [&>svg]:h-full [&>svg]:w-full"
            dangerouslySetInnerHTML={{ __html: styledAssets.svg }}
          />
        )}
        <div className="min-w-0 w-full flex-1 self-stretch divide-y divide-subtle">
          <CardRow label="Colours" className="pt-0">
            <span>{`${fgLabel} on ${bgLabel}`}</span>
          </CardRow>
          <CardRow label="Dot shape">
            <span>{dotShapeLabel}</span>
          </CardRow>
          <CardRow label="Centre logo">
            <span>
              {style.showLogo ? `Enabled (${style.logoScalePercent}% · ${style.logoLabel})` : 'Off'}
            </span>
          </CardRow>
          <CardRow label="X-dimension (size)">
            <QrXDimensionSelect
              selectId={`${cardTestId}-styled-x-dim`}
              xDimensionMm={props.xDimensionMm}
              onChangeXDimensionMm={props.onChangeXDimensionMm}
            />
          </CardRow>
          <QrPrintedDimensionsRow assets={styledAssets} />
          <CardRow label="Contrast ratio" className="pb-0">
            <span className="tabular-nums">{ruleEvaluation.contrastRatio.toFixed(1)}:1</span>
          </CardRow>
        </div>
      </div>
      {!modalOpen && (
        <QrRuleViolationAlert
          ruleEvaluation={ruleEvaluation}
          onResetSafeDefaults={props.onResetSafeDefaults}
        />
      )}
      <ToastNotification
        data-testid="qr-style-saved-status"
        message={savedNotice !== null && ruleEvaluation.valid ? savedNotice : null}
      />
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-subtle pt-3">
        <Badge variant="warning" data-testid="styled-qr-unverified-label">
          {STYLED_QR_DISCLAIMER_LABEL}
        </Badge>
        <QrDownloadButtons
          disabled={disableActions}
          onDownload={props.onDownloadStyled}
          onOpenEditStyle={() => setModalOpen(true)}
        />
      </div>
      <QrStyleEditorModal
        open={modalOpen}
        cardTestId={cardTestId}
        style={style}
        styledAssets={styledAssets}
        ruleEvaluation={ruleEvaluation}
        disableActions={disableActions}
        onClose={() => setModalOpen(false)}
        onUpdateStyle={props.onUpdateStyle}
        onResetSafeDefaults={props.onResetSafeDefaults}
        onSaveStyled={props.onSaveStyled}
      />
    </div>
  );
}
